/**
 * ShareWorkoutDialog
 *
 * Shares a saved running FIT file. First asks WHERE to share:
 *   • Entire community  → POST /running/community/workouts/share  (shareRunningWorkout)
 *   • A club            → POST /clubs/{clubId}/files              (contributeFile)
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControlLabel, Switch,
  Box, Typography, Alert, CircularProgress,
  List, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { shareRunningWorkout } from '../../api/runningApi';
import { getMyClubs, contributeFile } from '../../api/clubApi';

export default function ShareWorkoutDialog({ open, onClose, file, onShared }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [step, setStep] = useState('choose'); // 'choose' | 'community' | 'club'
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  // Reset to the chooser and pre-fill the community title when the dialog opens.
  useEffect(() => {
    if (open && file) {
      setStep('choose');
      setDisplayName(file.race_name || file.location_name || file.filename || '');
      setDescription('');
      setIsAnonymous(false);
      setError(null);
      setSuccess(false);
      setSuccessMsg('');
    }
  }, [open, file]);

  // Load the user's clubs when they pick the club branch.
  useEffect(() => {
    if (open && step === 'club') {
      setClubsLoading(true);
      getMyClubs().then((d) => setClubs(d || [])).catch(() => setClubs([])).finally(() => setClubsLoading(false));
    }
  }, [open, step]);

  const handleShareCommunity = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      await shareRunningWorkout({
        original_file_id: file.id,
        display_name: displayName.trim() || null,
        description: description.trim() || null,
        is_anonymous: isAnonymous,
        race_id: file.race_id || null,
      });
      setSuccessMsg('It is now visible in the Community tab for others to download and use for comparison.');
      setSuccess(true);
      onShared?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to share workout');
    } finally {
      setLoading(false);
    }
  };

  const handleShareClub = async (clubId, club) => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      await contributeFile(clubId, file.id);
      setSuccessMsg(`It is now available to members of ${club?.name || 'your club'} in the club's files.`);
      setSuccess(true);
      onShared?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to share workout to club');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('choose');
    setDisplayName('');
    setDescription('');
    setIsAnonymous(false);
    setError(null);
    setSuccess(false);
    setSuccessMsg('');
    onClose();
  };

  const titleText = step === 'club' ? 'Share with a Club'
    : step === 'community' ? 'Share with Community'
    : 'Share Workout';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {step === 'club' ? <GroupsIcon fontSize="small" /> : <PeopleIcon fontSize="small" />}
        {titleText}
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ mt: 1 }}>
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600}>Workout shared successfully!</Typography>
              <Typography variant="caption">{successMsg}</Typography>
            </Alert>
          </Box>
        ) : step === 'choose' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Where would you like to share <strong>{file?.filename}</strong>?
            </Typography>
            <Button
              variant="outlined" size="large" startIcon={<GroupsIcon />}
              onClick={() => setStep('club')}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.25 }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>A club</Typography>
                <Typography variant="caption" color="text.secondary">Only members of a club you choose.</Typography>
              </Box>
            </Button>
            <Button
              variant="outlined" size="large" startIcon={<PeopleIcon />}
              onClick={() => setStep('community')}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.25 }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>Entire community</Typography>
                <Typography variant="caption" color="text.secondary">Any mkpace user can find and download it.</Typography>
              </Box>
            </Button>
          </Box>
        ) : step === 'club' ? (
          <Box sx={{ mt: 1 }}>
            {clubsLoading ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
            ) : clubs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">You're not a member of any club yet.</Typography>
            ) : (
              <List dense>
                {clubs.map((club) => (
                  <ListItemButton key={club.id} disabled={loading} onClick={() => handleShareClub(club.id, club)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: club.color || colors.blueAccent[400] }} />
                    </ListItemIcon>
                    <ListItemText primary={club.name} secondary={`${club.member_count} member${club.member_count !== 1 ? 's' : ''}`} />
                  </ListItemButton>
                ))}
              </List>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>{error}</Alert>}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sharing <strong>{file?.filename}</strong> with the community lets other runners
              download it and use it as a reference workout for comparison.
              {file?.race_name && (<> It will be linked to <strong>{file.race_name}</strong>.</>)}
            </Typography>

            <TextField
              label="Display Name (optional)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth size="small"
              placeholder={file?.filename || 'My Trail Run'}
              helperText="Public title shown in the community list"
            />

            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline rows={3} fullWidth size="small"
              placeholder="Race conditions, notes about the effort…"
            />

            <FormControlLabel
              control={<Switch checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Share anonymously (hide my name)</Typography>}
            />

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {success ? (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        ) : step === 'choose' ? (
          <Button onClick={handleClose}>Cancel</Button>
        ) : step === 'club' ? (
          <>
            <Button onClick={() => setStep('choose')} disabled={loading}>Back</Button>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('choose')} disabled={loading}>Back</Button>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleShareCommunity}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <PeopleIcon />}
            >
              {loading ? 'Sharing…' : 'Share with Community'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
